// Quick Add Popup - Simplified variant selector popup
(() => {
  // Enable debug mode
  const DEBUG = true;
  
  // Debug log function
  function debug(...args) {
    if (DEBUG) {
      console.log('[Quick-Add-Popup]', ...args);
    }
  }
  
  debug('Script loaded');
  
  // Wait for both DOM and Shopify theme JS to be fully loaded
  document.addEventListener('DOMContentLoaded', () => {
    debug('DOM loaded, initializing popup with delay');
    // Delay initialization slightly to ensure all Shopify scripts are ready
    setTimeout(initQuickAddPopup, 100);
    
    // Disable the original quick-add-modal functionality
    disableOriginalQuickAdd();
    
    // Clean up event listeners when the page unloads
    window.addEventListener('unload', () => {
      window.removeEventListener('resize', handleResize);
    });
  });

  // Function to disable the original quick-add-modal
  function disableOriginalQuickAdd() {
    debug('Disabling original quick-add functionality');
    
    // Override the show method of quick-add-modal if it exists
    if (customElements.get('quick-add-modal')) {
      debug('Found quick-add-modal custom element, overriding show method');
      
      const originalProto = customElements.get('quick-add-modal').prototype;
      const originalShow = originalProto.show;
      
      // Only override for multi-variant products
      originalProto.show = function(opener) {
        // Check if this is a multi-variant product
        const modalId = opener.getAttribute('data-modal');
        if (modalId && modalId.startsWith('#QuickAdd-')) {
          debug('Intercepted quick-add-modal show for multi-variant product');
          
          // Reset the button state but don't actually show the modal
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          const spinner = opener.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
          
          return; // Don't show the modal
        }
        
        // For other cases (like quick-view), use the original method
        return originalShow.call(this, opener);
      };
    }
    
    // Hide any existing quick-add-modal elements
    document.querySelectorAll('quick-add-modal').forEach(modal => {
      modal.style.display = 'none';
      debug('Hidden quick-add-modal:', modal.id);
    });
  }

  // Store clicked button for reference
  let currentButton = null;

  // Handle window resize events for responsive popup
  function handleResize() {
    const popup = document.getElementById('quickAddPopupContainer');
    if (popup) {
      if (window.innerWidth >= 768) {
        popup.classList.add('desktop-view');
      } else {
        popup.classList.remove('desktop-view');
      }
    }
  }

  // Main initialization function
  function initQuickAddPopup() {
    debug('Initializing Quick-Add-Popup');
    
    // Listen for window resize events
    window.addEventListener('resize', handleResize);
    
    // Set up a global event listener for all modal-opener elements
    document.body.addEventListener('click', (event) => {
      // Check if the click is on a Choose Options button or its parent modal-opener
      const modalOpener = event.target.closest('modal-opener[data-modal^="#QuickAdd-"]');
      const button = event.target.closest('.quick-add__submit');
      
      if (modalOpener && button) {
        debug('Quick-add button clicked', modalOpener, button);
        
        // Get product data
        const productUrl = button.getAttribute('data-product-url');
        const modalId = modalOpener.getAttribute('data-modal');
        const productId = modalId ? modalId.replace('#QuickAdd-', '') : null;
        
        if (productUrl && productId) {
          debug('Product data found', { productUrl, productId });
          
          // Prevent default behavior and stop propagation
          event.preventDefault();
          event.stopPropagation();
          
          // Store button reference
          currentButton = button;
          
          // Show loading state
          toggleLoadingState(button, true);
          
          // Fetch the product data and show popup
          fetchProductVariantsAndShowPopup(productUrl, productId, button);
          
          return false; // Ensure event is fully canceled
        } else {
          debug('Missing product data', { productUrl, modalId });
        }
      }
    }, true); // Use capture phase to intercept events before other handlers
  
    // Create a global popup container that we'll reuse
    if (!document.getElementById('quickAddPopupContainer')) {
      debug('Creating popup container');
      const popupContainer = document.createElement('div');
      popupContainer.id = 'quickAddPopupContainer';
      popupContainer.className = 'quick-add-popup';
      
      // Add desktop class if on desktop
      if (window.innerWidth >= 768) {
        popupContainer.classList.add('desktop-view');
      }
      
      document.body.appendChild(popupContainer);
      
      // Close popup when clicking outside content
      popupContainer.addEventListener('click', (e) => {
        if (e.target === popupContainer) {
          debug('Closing popup (background click)');
          closePopup();
        }
      });
      
      // Close popup when pressing Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popupContainer.classList.contains('active')) {
          debug('Closing popup (Escape key)');
          closePopup();
        }
      });
      
      debug('Popup container created and added to DOM');
    }
  }

  // Toggle loading state on a button
  function toggleLoadingState(button, isLoading) {
    if (!button) {
      debug('No button provided to toggleLoadingState');
      return;
    }
    
    debug('Toggle loading state', isLoading);
    button.disabled = isLoading;
    
    if (isLoading) {
      button.classList.add('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    } else {
      button.classList.remove('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    }
  }

  // Fetch product variants and show the popup
  function fetchProductVariantsAndShowPopup(productUrl, productId, button) {
    debug('Fetching product data', productUrl);
    
    // Extract product handle from URL for later API calls
    const productHandle = productUrl.split('/').filter(Boolean).pop();
    debug('Extracted product handle for API calls:', productHandle);
    
    fetch(productUrl)
      .then(response => {
        debug('Product fetch response', response.ok);
        return response.text();
      })
      .then(html => {
        debug('Product HTML received, parsing');
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Get the variant data
        const variantSelects = doc.querySelector('variant-selects');
        const variantRadios = doc.querySelector('variant-radios');
        const variantContainer = variantSelects || variantRadios;
        
        debug('Variant containers found', { 
          variantSelects: !!variantSelects, 
          variantRadios: !!variantRadios 
        });
        
        let selectedVariant = null;
        const variantJson = variantContainer?.querySelector('[data-selected-variant]')?.textContent;
        
        if (variantJson) {
          try {
            selectedVariant = JSON.parse(variantJson);
            debug('Selected variant parsed', selectedVariant);
          } catch (e) {
            debug('Error parsing variant JSON', e);
          }
        }
        
        // Get all variants and product data
        let productVariantsJson = null;
        
        // Try to find product data in multiple places
        const variantsScript = doc.querySelector('script[type="application/json"][data-product-json]');
        const productDataScript = doc.querySelector('script#ProductJson-product-template');
        
        if (variantsScript) {
          try {
            productVariantsJson = JSON.parse(variantsScript.textContent);
            debug('Product variants JSON parsed from data-product-json', productVariantsJson);
          } catch (e) {
            debug('Error parsing product JSON from data-product-json', e);
          }
        } 
        
        // If we still don't have product data, try alternative source
        if (!productVariantsJson || !productVariantsJson.product) {
          if (productDataScript) {
            try {
              productVariantsJson = { product: JSON.parse(productDataScript.textContent) };
              debug('Product JSON parsed from ProductJson-product-template', productVariantsJson);
            } catch (e) {
              debug('Error parsing product JSON from ProductJson-product-template', e);
            }
          }
        }
        
        // Try to extract product name directly if still missing
        if (!productVariantsJson || !productVariantsJson.product || !productVariantsJson.product.title) {
          const productTitle = doc.querySelector('.product__title h1')?.textContent 
                            || doc.querySelector('.product-title')?.textContent
                            || doc.querySelector('h1.title')?.textContent;
          
          if (productTitle) {
            debug('Extracted product title directly from HTML:', productTitle);
            productVariantsJson = productVariantsJson || {};
            productVariantsJson.product = productVariantsJson.product || {};
            productVariantsJson.product.title = productTitle;
          }
        }
        
        // Extract variant options
        const options = [];
        
        if (variantContainer) {
          variantContainer.querySelectorAll('.product-form__input').forEach(input => {
            const label = input.querySelector('.form__label')?.textContent || '';
            let optionValues = [];
            
            // Handle select dropdowns
            const select = input.querySelector('select');
            if (select) {
              optionValues = Array.from(select.querySelectorAll('option')).map(option => ({
                value: option.value,
                text: option.textContent,
                disabled: option.disabled,
                selected: option.selected
              }));
            }
            
            // Handle radio buttons
            const radios = input.querySelectorAll('input[type="radio"]');
            if (radios.length) {
              optionValues = Array.from(radios).map(radio => ({
                value: radio.value,
                text: radio.nextElementSibling?.textContent || radio.value,
                disabled: radio.disabled,
                selected: radio.checked
              }));
            }
            
            if (optionValues.length) {
              options.push({
                name: label,
                values: optionValues
              });
            }
          });
        }
        
        debug('Extracted variant options', options);
        
        // If we still don't have product data, fetch it directly
        if (!productVariantsJson || !productVariantsJson.product) {
          debug('No product data found in HTML, fetching from API');
          fetch(`/products/${productHandle}.js`)
            .then(response => response.json())
            .then(productData => {
              debug('Product data fetched from API', productData);
              productVariantsJson = { product: productData };
              createQuickAddPopup(productId, options, selectedVariant, productVariantsJson, productHandle);
            })
            .catch(error => {
              debug('Error fetching product data from API:', error);
              createQuickAddPopup(productId, options, selectedVariant, null, productHandle);
            });
        } else {
          // Create and show popup with the data we have
          createQuickAddPopup(productId, options, selectedVariant, productVariantsJson, productHandle);
        }
      })
      .catch(error => {
        debug('Error fetching product data', error);
        // Reset button state
        toggleLoadingState(button, false);
      });
  }

  // Create and display the popup
  function createQuickAddPopup(productId, options, selectedVariant, productJson, productHandle) {
    // Get the popup container
    const popup = document.getElementById('quickAddPopupContainer');
    if (!popup) return;
    
    debug('Creating quick add popup with productJson:', productJson);
    
    // Clear previous content
    popup.innerHTML = '';
    popup.setAttribute('data-product-id', productId);
    
    // Store product handle for API calls
    if (productHandle) {
      popup.setAttribute('data-product-handle', productHandle);
    }
    
    // Create popup content
    const content = document.createElement('div');
    content.className = 'quick-add-popup__content';
    
    // Close button
    const closeButton = document.createElement('button');
    closeButton.className = 'quick-add-popup__close';
    closeButton.innerHTML = '<svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/></svg>';
    closeButton.setAttribute('aria-label', 'Close');
    closeButton.addEventListener('click', closePopup);
    
    // Create product info section - always create this section
    const productInfoSection = document.createElement('div');
    productInfoSection.className = 'quick-add-popup__product-info';
    productInfoSection.style.display = 'flex'; // Ensure it's visible
    productInfoSection.style.flexDirection = 'column';
    productInfoSection.style.alignItems = 'center';
    
    let productTitle = 'Select Options';
    let productPrice = '';
    
    // Get product title and price if available
    if (productJson && productJson.product) {
      debug('Product data found, adding title and price:', productJson.product.title);
      productTitle = productJson.product.title || 'Select Options';
      
      // Get price data
      if (selectedVariant) {
        debug('Selected variant found, using its price:', selectedVariant.price);
        productPrice = formatMoney(selectedVariant.price);
      } else if (productJson.product.variants && productJson.product.variants.length > 0) {
        // Use first variant price as fallback
        debug('No selected variant, using first variant price:', productJson.product.variants[0].price);
        productPrice = formatMoney(productJson.product.variants[0].price);
      }
    } else {
      debug('Product data missing or incomplete:', productJson);
    }
    
    // Add title element
    const titleElement = document.createElement('h3');
    titleElement.className = 'quick-add-popup__title';
    titleElement.textContent = productTitle;
    titleElement.style.fontSize = '20px';
    titleElement.style.fontWeight = 'bold';
    titleElement.style.margin = '0 0 0px 0';
    titleElement.style.color = '#000';
    titleElement.style.width = '100%';
    titleElement.style.textAlign = 'center';
    productInfoSection.appendChild(titleElement);
    
    // Add price element if price is available
    if (productPrice) {
      const priceElement = document.createElement('div');
      priceElement.className = 'quick-add-popup__initial-price';
      priceElement.textContent = productPrice;
      priceElement.style.fontSize = '18px';
      priceElement.style.fontWeight = '600';
      priceElement.style.color = '#000000';
      priceElement.style.padding = '4px 12px';
      priceElement.style.backgroundColor = '#FFFFFF';
      priceElement.style.borderRadius = '4px';
      priceElement.style.display = 'inline-block';
      priceElement.style.marginBottom = '8px';
      productInfoSection.appendChild(priceElement);
    }
    
    // Add separator line
    const separator = document.createElement('div');
    separator.style.width = '100%';
    separator.style.height = '1px';
    separator.style.backgroundColor = '#e6e6e6';
    separator.style.margin = '8px 0 16px 0';
    productInfoSection.appendChild(separator);
    
    // Variants container
    const variantsContainer = document.createElement('div');
    variantsContainer.className = 'quick-add-popup__variants';
    
    // Create variant button groups for each option
    options.forEach((option, index) => {
      // Create a fieldset for this option group
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'quick-add-popup__option-fieldset';
      fieldset.setAttribute('data-option-index', index);
      
      // Add option name as a legend
      const legend = document.createElement('legend');
      legend.className = 'quick-add-popup__option-legend';
      legend.textContent = option.name;
      fieldset.appendChild(legend);
      
      // Create a div to hold the variant buttons
      const buttonGroup = document.createElement('div');
      buttonGroup.className = 'quick-add-popup__option-buttons';
      
      // Create radio buttons + labels for each value
      option.values.forEach((value, valueIndex) => {
        const inputId = `quick-add-option-${productId}-${index}-${valueIndex}`;
        const inputName = `quick-add-option-${productId}-${index}`;
        
        // Create radio input (hidden)
        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = inputName;
        input.value = value.value;
        input.checked = value.selected;
        input.className = value.disabled ? 'disabled' : '';
        input.setAttribute('data-option-index', index);
        
        // Create label (visible button)
        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.textContent = value.text;
        if (value.disabled) {
          // Don't show "Unavailable" text for sold out variants
          label.innerHTML = `${value.text}`;
          label.classList.add('option-sold-out');
        }
        
        // Add event listener to handle variant selection
        input.addEventListener('change', () => {
          updateVariantFromButtons(popup, productId, productJson);
        });
        
        // Add to button group
        buttonGroup.appendChild(input);
        buttonGroup.appendChild(label);
      });
      
      // Add button group to fieldset
      fieldset.appendChild(buttonGroup);
      
      // Add fieldset to variants container
      variantsContainer.appendChild(fieldset);
    });
    
    // Create a hidden element to store variant availability info
    // This replaces the visible variant info that showed redundant price
    const variantInfo = document.createElement('div');
    variantInfo.className = 'quick-add-popup__variant-info';
    variantInfo.style.display = 'none'; // Hide this element, we'll use it to store data
    variantsContainer.appendChild(variantInfo);
    
    // Add to cart button
    const submitButton = document.createElement('button');
    submitButton.className = 'quick-add-popup__submit';
    submitButton.type = 'button';
    submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
    submitButton.style.backgroundColor = '#E54821';
    submitButton.style.color = '#ffffff';
    submitButton.style.border = 'none';
    submitButton.style.width = '100%';
    submitButton.style.padding = '10px 18px';
    submitButton.style.fontSize = '16px';
    submitButton.style.fontWeight = 'bold';
    submitButton.style.cursor = 'pointer';
    submitButton.style.borderRadius = '0';
    submitButton.style.margin = '15px 0 0 0';
    submitButton.style.transition = 'all 0.2s ease-in-out';
    
    // Add styles for sold-out button state
    const style = document.createElement('style');
    style.textContent = `
      .quick-add-popup__submit.button--disabled.sold-out {
        background-color: #999999 !important;
        cursor: not-allowed;
        opacity: 0.65;
        color: #ffffff !important;
        border: none !important;
      }
      .option-sold-out {
        opacity: 0.7;
        position: relative;
      }
      .quick-add-popup__variant-info {
        margin-top: 10px;
        font-weight: bold;
      }
      .sold-out-text {
        color: #999999;
        font-weight: bold;
      }
    `;
    document.head.appendChild(style);
    
    // Make sure any previous event listeners are removed
    submitButton.removeEventListener('click', submitButton.clickHandler);
    
    // Define a handler function that we can reference later
    submitButton.clickHandler = () => {
      debug('Add to cart button clicked');
      // Prevent adding to cart when button is disabled
      if (submitButton.disabled || submitButton.classList.contains('button--disabled')) {
        debug('Button is disabled, not adding to cart');
        return;
      }
      addToCartFromPopup(popup, productId);
    };
    
    // Add the event listener
    submitButton.addEventListener('click', submitButton.clickHandler);
    
    // Assemble and append popup
    content.appendChild(closeButton);
    content.appendChild(productInfoSection);
    content.appendChild(variantsContainer);
    content.appendChild(submitButton);
    popup.appendChild(content);
    
    // Prevent scrolling on body when popup is open
    document.body.style.overflow = 'hidden';
    
    // Show popup with animation - using RAF for smoother transition
    // Add a class to detect if we're on mobile or desktop for proper animations
    if (window.innerWidth >= 768) {
      popup.classList.add('desktop-view');
    } else {
      popup.classList.remove('desktop-view');
    }
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        popup.classList.add('active');
      });
    });
    
    // Initialize variant selection
    updateVariantFromButtons(popup, productId, productJson);
    
    // Reset original button state
    toggleLoadingState(currentButton, false);
  }

  // Update selected variant based on button selections
  function updateVariantFromButtons(popup, productId, productJson) {
    const radioButtons = popup.querySelectorAll('input[type="radio"]:checked');
    const submitButton = popup.querySelector('.quick-add-popup__submit');
    const variantInfo = popup.querySelector('.quick-add-popup__variant-info');
    const loadingSpinner = submitButton.querySelector('.loading__spinner');
    if (loadingSpinner) loadingSpinner.classList.remove('hidden');
    submitButton.disabled = true;

    // Get selected options from radio buttons
    const selectedOptions = Array.from(radioButtons).map(radio => radio.value);
    debug('Selected options from buttons', selectedOptions);

    // If we don't have enough selected options, show an error
    // Fix: Require all options to be selected
    let totalOptions = 0;
    if (productJson && productJson.product && productJson.product.options) {
      totalOptions = productJson.product.options.length;
    } else if (popup.querySelectorAll('.quick-add-popup__option-fieldset').length) {
      totalOptions = popup.querySelectorAll('.quick-add-popup__option-fieldset').length;
    }
    if (selectedOptions.length !== totalOptions) {
      debug('Not all options selected');
      submitButton.disabled = false;
      submitButton.innerHTML = 'Please select all options <span class="loading__spinner hidden"></span>';
      submitButton.removeAttribute('data-variant-id');
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      return;
    }

    // If we have the product JSON data already, find the matching variant without a fetch
    if (productJson && productJson.product && productJson.product.variants) {
      const variants = productJson.product.variants;
      debug('Looking for matching variant among', variants.length, 'variants');
      // Fix: Match all options exactly
      const selectedVariant = variants.find(variant => {
        return variant.options.length === selectedOptions.length &&
          variant.options.every((option, index) => option === selectedOptions[index]);
      });
      debug('Found matching variant:', selectedVariant ? selectedVariant.id : 'none');
      updateVariantUI(selectedVariant, submitButton, variantInfo);
      return;
    }
    
    // If we don't have product data but the variant ID is already on the button, 
    // don't make an API call if nothing has changed
    if (submitButton.hasAttribute('data-variant-id') && !productJson) {
      debug('Button already has variant ID and no new product data');
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      submitButton.disabled = false;
      return;
    }
    
    // Otherwise, fetch the product data to find the matching variant
    // Get product handle for API call
    const productHandle = popup.getAttribute('data-product-handle');
    
    if (!productHandle) {
      debug('No product handle for API call, trying to use product ID');
      // Fallback for backward compatibility
      submitButton.disabled = false;
      submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
      if (loadingSpinner) loadingSpinner.classList.add('hidden');
      return;
    }
    
    debug('Fetching product data from API using handle:', productHandle);
    fetch(`/products/${productHandle}.js`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to fetch product data: ${response.status} ${response.statusText}`);
        }
        return response.json();
      })
      .then(productData => {
        const variants = productData.variants;
        debug('Fetched product data with', variants.length, 'variants');
        const selectedVariant = variants.find(variant => {
          return variant.options.length === selectedOptions.length &&
            variant.options.every((option, index) => option === selectedOptions[index]);
        });
        debug('Found matching variant:', selectedVariant ? selectedVariant.id : 'none');
        updateVariantUI(selectedVariant, submitButton, variantInfo);
      })
      .catch(error => {
        debug('Error updating variant:', error);
        submitButton.disabled = false;
        submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
        if (loadingSpinner) loadingSpinner.classList.add('hidden');
      });
  }
  
  // Update the UI based on selected variant
  function updateVariantUI(selectedVariant, submitButton, variantInfo) {
    const loadingSpinner = submitButton.querySelector('.loading__spinner');
    const popup = document.getElementById('quickAddPopupContainer');
    const productInfoPrice = popup.querySelector('.quick-add-popup__initial-price');
    debug('Updating variant UI with variant:', selectedVariant);
    debug('Product info price element found:', !!productInfoPrice);
    
    // Update the main price display at the top of the popup
    if (productInfoPrice && selectedVariant) {
      const formattedPrice = formatMoney(selectedVariant.price);
      debug('Updating price display to:', formattedPrice);
      productInfoPrice.textContent = formattedPrice;
    } else if (!productInfoPrice && selectedVariant) {
      debug('Price element not found, creating it');
      const productInfoSection = popup.querySelector('.quick-add-popup__product-info');
      if (productInfoSection) {
        const priceElement = document.createElement('div');
        priceElement.className = 'quick-add-popup__initial-price';
        priceElement.textContent = formatMoney(selectedVariant.price);
        priceElement.style.fontSize = '18px';
        priceElement.style.fontWeight = '600';
        priceElement.style.color = '#000000';
        priceElement.style.padding = '4px 12px';
        priceElement.style.backgroundColor = '#ffffff';
        priceElement.style.borderRadius = '4px';
        priceElement.style.display = 'inline-block';
        priceElement.style.marginBottom = '8px';
        const separator = productInfoSection.querySelector('div:not([class])');
        if (separator) {
          productInfoSection.insertBefore(priceElement, separator);
        } else {
          productInfoSection.appendChild(priceElement);
        }
      }
    }
    
    // Store variant availability info in the hidden variantInfo element
    // instead of displaying it (no visual price here anymore)
    if (variantInfo) {
      variantInfo.innerHTML = '';
      variantInfo.style.display = 'none'; // Ensure it's hidden
      
      if (selectedVariant) {
        if (selectedVariant.available) {
          variantInfo.setAttribute('data-variant-available', 'true');
        } else {
          variantInfo.setAttribute('data-variant-available', 'false');
        }
      }
    }
    
    // Update the Add to Cart button state based on the selected variant
    if (selectedVariant && selectedVariant.available) {
      debug('Setting available variant ID on button:', selectedVariant.id);
      submitButton.disabled = false;
      submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
      submitButton.setAttribute('data-variant-id', selectedVariant.id);
      submitButton.classList.remove('button--disabled', 'sold-out');
    } else if (selectedVariant && !selectedVariant.available) {
      debug('Variant is sold out, disabling button');
      submitButton.disabled = true;
      submitButton.innerHTML = 'Sold Out';
      submitButton.removeAttribute('data-variant-id');
      submitButton.classList.add('button--disabled', 'sold-out');
    } else {
      debug('No valid variant found, showing select options');
      submitButton.disabled = true;
      submitButton.innerHTML = 'Select Options';
      submitButton.removeAttribute('data-variant-id');
      submitButton.classList.add('button--disabled');
    }
    
    if (loadingSpinner) loadingSpinner.classList.add('hidden');
  }
  
  // Format money value
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = '${{amount}}';
    
    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      
      if (isNaN(number) || number == null) {
        return 0;
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';
      
      return dollarsAmount + centsAmount;
    }
    
    switch(formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
    }
    
    return formatString.replace(placeholderRegex, value);
  }

  // Add to cart functionality from popup
  function addToCartFromPopup(popup, productId) {
    const submitButton = popup.querySelector('.quick-add-popup__submit');
    
    // Get the variant ID from the button
    let variantId = submitButton.getAttribute('data-variant-id');
    debug('Add to cart clicked with variantId:', variantId);
    
    // If button is disabled or missing variant ID, show appropriate message
    if (submitButton.disabled || submitButton.classList.contains('button--disabled')) {
      debug('Button is disabled or no variant selected');
      return;
    }
    
    // If no variantId is selected, force update and try again
    if (!variantId) {
      debug('No variant ID found, forcing update');
      
      // First, check if all options are selected
      const selectedOptions = popup.querySelectorAll('input[type="radio"]:checked');
      const optionGroups = popup.querySelectorAll('.quick-add-popup__option-fieldset');
      
      if (selectedOptions.length < optionGroups.length) {
        // Not all options are selected
        submitButton.innerHTML = 'Please select all options <span class="loading__spinner hidden"></span>';
        
        // Reset after a short delay
        setTimeout(() => {
          submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
        }, 2000);
        return;
      }
      
      // Try to update the variant once more - this time with product data from button to prevent API call
      // Get the product handle for direct API calls
      const productHandle = popup.getAttribute('data-product-handle');
      
      if (!productHandle) {
        debug('No product handle available, cannot update variant');
        submitButton.innerHTML = 'Error: Cannot find product <span class="loading__spinner hidden"></span>';
        
        setTimeout(() => {
          submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
        }, 2000);
        return;
      }
      
      // Show loading state
      const loadingSpinner = submitButton.querySelector('.loading__spinner');
      if (loadingSpinner) loadingSpinner.classList.remove('hidden');
      submitButton.disabled = true;
      
      // Fetch product data directly
      fetch(`/products/${productHandle}.js`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch product data: ${response.status} ${response.statusText}`);
          }
          return response.json();
        })
        .then(productData => {
          // Create product JSON wrapper for updateVariantFromButtons
          const productJson = { product: productData };
          
          // Update variant selection with the fresh data
          updateVariantFromButtons(popup, productId, productJson);
          
          // Check if we have a variant ID after update
          setTimeout(() => {
            variantId = submitButton.getAttribute('data-variant-id');
            debug('After direct API update, variantId is:', variantId);
            
            if (variantId) {
              addToCartWithVariantId(popup, productId, variantId, submitButton);
            } else {
              submitButton.innerHTML = 'Please select a valid variant <span class="loading__spinner hidden"></span>';
              submitButton.disabled = false;
              
              // Reset after a short delay
              setTimeout(() => {
                submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
              }, 2000);
            }
          }, 100);
        })
        .catch(error => {
          debug('Error fetching product data for add to cart:', error);
          submitButton.disabled = false;
          submitButton.innerHTML = 'Error loading product <span class="loading__spinner hidden"></span>';
          
          setTimeout(() => {
            submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
          }, 2000);
        });
      
      return;
    }
    
    // We have a valid variant ID, proceed with adding to cart
    addToCartWithVariantId(popup, productId, variantId, submitButton);
  }
  
  // Helper function to add to cart with a known variant ID
  function addToCartWithVariantId(popup, productId, variantId, submitButton) {
    debug('Adding to cart with variant ID:', variantId);
    // Show loading state
    submitButton.disabled = true;
    const spinner = submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    // Add to cart via fetch API
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1
        }]
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      debug('Successfully added to cart:', data);
      // Show success feedback
      submitButton.textContent = 'Added!';
      // Update cart count
      updateCartCount();
      // Dispatch cart:item-added event for drawer highlight
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { variantId } }));
      
      // Improved cart drawer opening function with more reliable Shopify integration
      const openCartDrawer = () => {
        debug('Attempting to open cart drawer');
        
        // Method 1: Get the cart section using AJAX
        fetch('?section_id=cart-drawer')
          .then(response => response.text())
          .then(html => {
            // Try to find cart-drawer element
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              debug('Found cart-drawer, updating and opening it');
              
              // If cart drawer has a renderContents method, use it to update the drawer
              if (typeof cartDrawer.renderContents === 'function') {
                try {
                  // Parse sections from HTML
                  const parsedState = {
                    id: variantId,
                    sections: {
                      'cart-drawer': html
                    }
                  };
                  
                  // Important: Update the empty state before rendering contents
                  cartDrawer.classList.remove('is-empty');
                  
                  // Also update cart-drawer-items if it exists
                  const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
                  if (cartDrawerItems) {
                    cartDrawerItems.classList.remove('is-empty');
                  }
                  
                  cartDrawer.renderContents(parsedState);
                  return true;
                } catch (e) {
                  debug('Error using renderContents method:', e);
                }
              }
              
              // Set active element if the method exists
              if (typeof cartDrawer.setActiveElement === 'function' && submitButton) {
                cartDrawer.setActiveElement(submitButton);
              }
              
              // Remove the empty state class before opening
              cartDrawer.classList.remove('is-empty');
              
              // Also update cart-drawer-items if it exists
              const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
              if (cartDrawerItems) {
                cartDrawerItems.classList.remove('is-empty');
              }
              
              // Call the open method directly
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open(submitButton);
                return true;
              }
              
              // Alternative: Apply the classes that trigger the drawer opening
              cartDrawer.classList.add('animate', 'active');
              document.body.classList.add('overflow-hidden');
              return true;
            }
            
            // Method 2: Try to click the cart icon
            const cartIcon = document.querySelector('#cart-icon-bubble');
            if (cartIcon) {
              debug('Found cart icon, clicking it');
              try {
                cartIcon.click();
                return true;
              } catch (e) {
                debug('Error clicking cart icon:', e);
              }
            }
            
            // Method 3: Look for theme-specific cart drawer and update its content
            const themeCartDrawer = document.getElementById('CartDrawer');
            if (themeCartDrawer) {
              debug('Found theme cart drawer, updating content and opening');
              try {
                const drawerInner = themeCartDrawer.querySelector('.drawer__inner');
                if (drawerInner) {
                  // Remove the empty state classes
                  drawerInner.classList.remove('drawer__inner-empty');
                  drawerInner.closest('cart-drawer')?.classList.remove('is-empty');
                  
                  // Update cart-drawer-items if it exists
                  const cartDrawerItems = drawerInner.querySelector('cart-drawer-items');
                  if (cartDrawerItems) {
                    cartDrawerItems.classList.remove('is-empty');
                  }
                  
                  // Update inner HTML
                  drawerInner.innerHTML = new DOMParser()
                    .parseFromString(html, 'text/html')
                    .querySelector('.drawer__inner')
                    ?.innerHTML || drawerInner.innerHTML;
                }
                themeCartDrawer.classList.add('animate', 'active');
                document.body.classList.add('overflow-hidden');
                return true;
              } catch (e) {
                debug('Error updating and opening theme cart drawer:', e);
              }
            }
            
            return false;
          })
          .catch(e => {
            debug('Error fetching cart drawer section:', e);
            // Fallback to standard cart-drawer opening methods
            
            // Try the cart-drawer element
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              try {
                // Remove the empty state class before opening
                cartDrawer.classList.remove('is-empty');
                
                // Also update cart-drawer-items if it exists
                const cartDrawerItems = cartDrawer.querySelector('cart-drawer-items');
                if (cartDrawerItems) {
                  cartDrawerItems.classList.remove('is-empty');
                }
                
                if (typeof cartDrawer.setActiveElement === 'function' && submitButton) {
                  cartDrawer.setActiveElement(submitButton);
                }
                
                if (typeof cartDrawer.open === 'function') {
                  cartDrawer.open(submitButton);
                  return true;
                }
                
                cartDrawer.classList.add('animate', 'active');
                document.body.classList.add('overflow-hidden');
                return true;
              } catch (err) {
                debug('Error opening cart drawer:', err);
              }
            }
            
            // Try clicking the cart icon
            const cartIcon = document.querySelector('#cart-icon-bubble');
            if (cartIcon) {
              try {
                cartIcon.click();
                return true;
              } catch (err) {
                debug('Error clicking cart icon:', err);
              }
            }
            
            return false;
          });
        
        // Also dispatch cart:refresh event that other scripts might listen for
        try {
          document.dispatchEvent(new CustomEvent('cart:refresh', { 
            detail: { trigger: submitButton } 
          }));
        } catch (e) {
          debug('Error dispatching cart:refresh event:', e);
        }
        
        return true; // Indicate that we've started the process
      };
      
      // Force a delay of 100ms before opening the cart drawer to ensure 
      // the cart has been updated on the server side
      setTimeout(() => {
        openCartDrawer();
        
        // Close popup after delay
        setTimeout(() => {
          closePopup();
          // Show success message
          showSuccessMessage();
        }, 800);
      }, 100);
    })
    .catch(error => {
      console.error('Error adding to cart:', error);
      debug('Error adding to cart:', error);
      submitButton.textContent = 'Error';
      // Reset button state after delay
      setTimeout(() => {
        submitButton.disabled = false;
        submitButton.innerHTML = 'Add to Cart <span class="loading__spinner hidden"></span>';
      }, 2000);
    });
  }
  
  // Show success message
  function showSuccessMessage() {
    const successMessage = document.createElement('div');
    successMessage.className = 'quick-add-success-message';
    successMessage.innerHTML = '<div class="success-message-content"><span>✓</span> Added to cart!</div>';
    
    const content = successMessage.querySelector('.success-message-content');
    content.style.display = 'flex';
    content.style.alignItems = 'center';
    
    const checkmark = content.querySelector('span');
    checkmark.style.marginRight = '8px';
    
    document.body.appendChild(successMessage);
    
    setTimeout(() => {
      successMessage.style.opacity = '1';
    }, 10);
    
    setTimeout(() => {
      successMessage.style.opacity = '0';
      setTimeout(() => {
        successMessage.remove();
      }, 200);
    }, 2000);
  }

  // Update cart count in header
  function updateCartCount() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCount = document.querySelector('.cart-count-bubble');
        if (cartCount) {
          const count = cart.item_count;
          if (count > 0) {
            cartCount.textContent = count;
            cartCount.classList.remove('hidden');
          }
        }
        
        // Dispatch cart update event for other components
        document.dispatchEvent(new CustomEvent('cart:updated', { 
          detail: { cart }
        }));
      })
      .catch(e => console.error('Error updating cart count:', e));
  }

  // Close popup function
  function closePopup() {
    debug('Closing popup');
    const popup = document.getElementById('quickAddPopupContainer');
    if (popup) {
      popup.classList.remove('active');
      
      // Wait for animation to complete before restoring scrolling
      setTimeout(() => {
        // Restore scrolling
        document.body.style.overflow = '';
      }, 300); // Match the CSS transition time
      
      // Reset button state
      toggleLoadingState(currentButton, false);
      currentButton = null;
      
      debug('Popup closed, button reset');
    } else {
      debug('Popup container not found for closing');
    }
  }
})(); 

// Expose a global function to open cart drawer
// This helps other scripts or components integrate with the cart drawer
window.openCartDrawer = function(trigger) {
  console.log('[Quick-Add-Popup] Global openCartDrawer called');
  
  // Method 1: First try to get the cart drawer content through AJAX
  return fetch('?section_id=cart-drawer')
    .then(response => response.text())
    .then(html => {
      // Try to find cart-drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // If cart drawer has a renderContents method, use it to update the drawer
        if (typeof cartDrawer.renderContents === 'function') {
          try {
            // Parse sections from HTML
            const parsedState = {
              sections: {
                'cart-drawer': html
              }
            };
            cartDrawer.renderContents(parsedState);
            return true;
          } catch (e) {
            console.error('Error using renderContents method:', e);
          }
        }
        
        // Set active element if method exists
        if (typeof cartDrawer.setActiveElement === 'function' && trigger) {
          cartDrawer.setActiveElement(trigger);
        }
        
        // Call the open method
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open(trigger);
          return true;
        }
        
        // Alternative: Apply classes to open drawer
        cartDrawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
        return true;
      }
      
      // Method 2: Try clicking the cart icon
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon) {
        try {
          cartIcon.click();
          return true;
        } catch (e) {
          console.error('Error clicking cart icon:', e);
        }
      }
      
      // Method 3: Look for theme cart drawer and update it
      const themeCartDrawer = document.getElementById('CartDrawer');
      if (themeCartDrawer) {
        try {
          const drawerInner = themeCartDrawer.querySelector('.drawer__inner');
          if (drawerInner) {
            drawerInner.innerHTML = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('.drawer__inner')
              ?.innerHTML || drawerInner.innerHTML;
          }
          themeCartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          return true;
        } catch (e) {
          console.error('Error updating and opening theme cart drawer:', e);
        }
      }
      
      return false;
    })
    .catch(e => {
      console.error('Error fetching cart drawer section:', e);
      
      // Fallback to standard methods without AJAX update
      // Try the cart-drawer element
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        try {
          if (typeof cartDrawer.setActiveElement === 'function' && trigger) {
            cartDrawer.setActiveElement(trigger);
          }
          
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open(trigger);
            return true;
          }
          
          cartDrawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          return true;
        } catch (err) {
          console.error('Error opening cart drawer:', err);
        }
      }
      
      // Try clicking the cart icon
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (cartIcon) {
        try {
          cartIcon.click();
          return true;
        } catch (err) {
          console.error('Error clicking cart icon:', err);
        }
      }
      
      return false;
    });
}; 